import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { UserLayout } from '../../components/layout/UserLayout';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Ticket, CheckCircle2, Coins, Copy, Zap, Banknote, MapPin, Star, Phone } from 'lucide-react';

const EXCHANGE_PRESETS = ['Binance', 'Coinbase', 'Kraken', 'OKX', 'Bybit', 'Crypto.com', 'KuCoin', 'Bitget', 'Gate.io', 'Personal Wallet', 'Other'];

export const DepositPage = () => {
  const location = useLocation();
  
  // Use FocusedLayout when coming from Super App
  const fromSuperApp = location.state?.fromSuperApp !== false;
  const Layout = fromSuperApp ? FocusedLayout : UserLayout;
  
  const [platformCode, setPlatformCode] = useState('');
  const [externalCode, setExternalCode] = useState('');
  const [externalCurrency, setExternalCurrency] = useState('');
  const [externalAmount, setExternalAmount] = useState('');
  const [externalProviderId, setExternalProviderId] = useState('');
  const [currencies, setCurrencies] = useState([]);
  const [voucherProviders, setVoucherProviders] = useState([]);
  const [cryptoWallets, setCryptoWallets] = useState([]);
  const [cryptoWalletId, setCryptoWalletId] = useState('');
  const [cryptoAmount, setCryptoAmount] = useState('');
  const [cryptoSource, setCryptoSource] = useState('');
  const [cryptoSourceOther, setCryptoSourceOther] = useState('');
  const [cryptoTxHash, setCryptoTxHash] = useState('');
  const [cryptoNote, setCryptoNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [custodial, setCustodial] = useState(null);
  
  // Agent-assisted top up
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentAmount, setAgentAmount] = useState('');
  const [agentCurrency, setAgentCurrency] = useState('');
  const [agentMethod, setAgentMethod] = useState('cash');
  const [agentNote, setAgentNote] = useState('');

  useEffect(() => {
    publicAPI.getCurrencies(true).then((res) => setCurrencies(res.data.filter((c) => c.deposit_enabled)));
    userAPI.getVoucherProviders().then((res) => setVoucherProviders(res.data)).catch(() => {});
    userAPI.getCryptoDepositWallets().then((res) => setCryptoWallets(res.data)).catch(() => {});
    userAPI.getCustodialAddresses().then((res) => setCustodial(res.data)).catch(() => {});
    userAPI.browseAgents({ status: 'active', limit: 20 }).then((res) => setAgents(res.data.agents || [])).catch(() => {});
  }, []);

  const selectedProvider = voucherProviders.find((p) => p.id === externalProviderId);
  const selectedCryptoWallet = cryptoWallets.find((w) => w.id === cryptoWalletId);

  const submitPlatform = async (e) => {
    e.preventDefault();
    if (!platformCode.trim()) { toast.error('Please enter a voucher code'); return; }
    setLoading(true);
    try {
      const { data } = await userAPI.deposit(platformCode.trim(), 'platform');
      toast.success(data.message);
      setPlatformCode('');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Redemption failed');
    } finally { setLoading(false); }
  };

  const submitExternal = async (e) => {
    e.preventDefault();
    if (!externalProviderId) { toast.error('Please choose a voucher provider'); return; }
    if (!externalCode.trim() || !externalCurrency || !externalAmount || parseFloat(externalAmount) <= 0) {
      toast.error('Please fill in all fields with valid values');
      return;
    }
    setLoading(true);
    try {
      const { data } = await userAPI.deposit(externalCode.trim(), 'external', externalCurrency, parseFloat(externalAmount), externalProviderId);
      toast.success(data.message);
      setExternalCode('');
      setExternalAmount('');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Submission failed');
    } finally { setLoading(false); }
  };

  const submitCrypto = async (e) => {
    e.preventDefault();
    if (!cryptoWalletId) return toast.error('Pick a top-up wallet');
    if (!cryptoSource) return toast.error('Pick where the funds are coming from');
    if (cryptoSource === 'Other' && !cryptoSourceOther.trim()) return toast.error('Enter the custom source name');
    if (!cryptoAmount || parseFloat(cryptoAmount) <= 0) return toast.error('Enter an amount');
    if (!cryptoTxHash.trim()) return toast.error('Paste the transaction hash / link');
    setLoading(true);
    try {
      const { data } = await userAPI.submitCryptoDeposit({
        wallet_id: cryptoWalletId,
        amount: parseFloat(cryptoAmount),
        source: cryptoSource,
        source_other: cryptoSourceOther.trim(),
        tx_hash: cryptoTxHash.trim(),
        note: cryptoNote.trim(),
      });
      toast.success(data.message);
      setCryptoAmount(''); setCryptoTxHash(''); setCryptoNote(''); setCryptoSourceOther('');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Submission failed');
    } finally { setLoading(false); }
  };

  const copyAddress = (val) => {
    navigator.clipboard?.writeText(val).then(() => toast.success('Copied'));
  };
  
  const submitAgentDeposit = async (e) => {
    e.preventDefault();
    if (!selectedAgent) return toast.error('Please select an agent');
    if (!agentCurrency) return toast.error('Please select a currency');
    if (!agentAmount || parseFloat(agentAmount) <= 0) return toast.error('Enter a valid amount');
    setLoading(true);
    try {
      await userAPI.requestAgentDeposit({
        agent_id: selectedAgent.id,
        currency: agentCurrency,
        amount: parseFloat(agentAmount),
        method: agentMethod,
        note: agentNote.trim()
      });
      toast.success('Top-up request sent to agent! They will contact you shortly.');
      setAgentAmount(''); setAgentNote(''); setSelectedAgent(null);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const cryptoByCurrency = cryptoWallets.reduce((acc, w) => {
    (acc[w.currency] = acc[w.currency] || []).push(w);
    return acc;
  }, {});

  return (
    <Layout title="Top up">
      <div className="space-y-6" data-testid="deposit-page">
        <div>
          <h1 className="text-4xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Top up
          </h1>
          <p className="mt-2 text-muted-foreground">Add funds using vouchers or crypto</p>
        </div>

        <Tabs defaultValue={custodial ? 'auto' : 'platform'} className="max-w-2xl">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1">
            <TabsTrigger value="auto" data-testid="auto-crypto-tab" className="text-xs sm:text-sm"><Zap className="h-3 w-3 mr-1" /> Auto</TabsTrigger>
            <TabsTrigger value="platform" data-testid="platform-voucher-tab" className="text-xs sm:text-sm">Platform</TabsTrigger>
            <TabsTrigger value="external" data-testid="external-voucher-tab" className="text-xs sm:text-sm">External</TabsTrigger>
            <TabsTrigger value="crypto" data-testid="crypto-deposit-tab" className="text-xs sm:text-sm">Manual</TabsTrigger>
            <TabsTrigger value="agent" data-testid="agent-topup-tab" className="text-xs sm:text-sm"><Banknote className="h-3 w-3 mr-1" /> Agent</TabsTrigger>
          </TabsList>

          <TabsContent value="auto" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle>Instant Crypto Top up</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Send crypto to your personal address — auto-credited within a minute.</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!custodial ? (
                  <p className="text-center py-6 text-muted-foreground text-sm">Loading your wallet address…</p>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 space-y-2" data-testid="custodial-wallet-panel">
                      <p className="text-xs text-muted-foreground uppercase tracking-widest">Your personal wallet address</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs font-mono bg-background rounded px-2 py-2 break-all" data-testid="custodial-address">{custodial.address}</code>
                        <Button type="button" size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(custodial.address); toast.success('Address copied'); }} data-testid="copy-custodial-btn"><Copy className="h-4 w-4" /></Button>
                      </div>
                      <p className="text-xs text-muted-foreground">{custodial.note}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-2">Supported networks</p>
                      <div className="flex flex-wrap gap-2">
                        {(custodial.chains || []).map((c, idx) => (
                          <a key={`${c.key}-${idx}`} href={`${c.block_explorer_addr}${custodial.address}`} target="_blank" rel="noopener noreferrer"
                             className="text-xs px-3 py-1.5 rounded-full bg-muted border border-border hover:bg-muted/70">
                            {c.name} <span className="text-muted-foreground">→ explorer</span>
                          </a>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-2">Supported tokens ({(custodial.supported_tokens || []).length})</p>
                      <div className="grid gap-1 max-h-52 overflow-y-auto">
                        {(custodial.supported_tokens || []).map((t, i) => (
                          <div key={`${t.chain}-${t.symbol}-${i}`} className="text-xs flex items-center justify-between p-2 rounded bg-muted/50">
                            <span className="font-mono"><strong className="text-foreground">{t.symbol}</strong> · {t.chain}</span>
                            <span className="text-muted-foreground">min {t.min_topup || t.min_deposit || 0}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900">
                      <p className="text-sm text-yellow-800 dark:text-yellow-300">
                        <strong>Warning:</strong> This address only works on the listed networks. Sending unsupported tokens or using the wrong network will result in permanent loss. Top-ups below the minimum amount will not be credited.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="platform" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-950 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle>Platform Voucher</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Instantly credited to your wallet</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitPlatform} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Voucher Code</Label>
                    <Input
                      value={platformCode}
                      onChange={(e) => setPlatformCode(e.target.value.toUpperCase())}
                      placeholder="Enter voucher code"
                      data-testid="platform-voucher-input"
                      className="h-11 font-mono"
                    />
                    <p className="text-xs text-muted-foreground">Platform vouchers must already exist in the system</p>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-11" data-testid="redeem-platform-voucher-button">
                    {loading ? 'Redeeming...' : 'Redeem Voucher'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="external" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-yellow-100 dark:bg-yellow-950 flex items-center justify-center">
                    <Ticket className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <CardTitle>External Voucher</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Submitted for administrator approval</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitExternal} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Voucher Provider</Label>
                    <Select value={externalProviderId} onValueChange={setExternalProviderId}>
                      <SelectTrigger data-testid="external-provider-select" className="h-11">
                        <SelectValue placeholder={voucherProviders.length ? 'Choose a provider (e.g. OTT, 1Voucher)' : 'No providers available'} />
                      </SelectTrigger>
                      <SelectContent>
                        {voucherProviders.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedProvider?.instructions && (
                      <p className="text-xs text-muted-foreground bg-muted/60 rounded p-2 mt-1 whitespace-pre-wrap">
                        {selectedProvider.instructions}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Voucher Code / Reference</Label>
                    <Input
                      value={externalCode}
                      onChange={(e) => setExternalCode(e.target.value.toUpperCase())}
                      placeholder="Enter external voucher code"
                      data-testid="external-voucher-input"
                      className="h-11 font-mono"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={externalCurrency} onValueChange={setExternalCurrency}>
                      <SelectTrigger data-testid="external-currency-select" className="h-11">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c.symbol} value={c.symbol}>{c.symbol} - {c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input
                      type="number" step="0.01" min="0.01"
                      value={externalAmount}
                      onChange={(e) => setExternalAmount(e.target.value)}
                      placeholder="0.00"
                      data-testid="external-amount-input"
                      className="h-11"
                      required
                    />
                  </div>
                  <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      Your request will be reviewed by an administrator. Funds will be credited to your wallet once approved.
                    </p>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-11" data-testid="submit-external-voucher-button">
                    {loading ? 'Submitting...' : 'Submit for Approval'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="crypto" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-orange-100 dark:bg-orange-950 flex items-center justify-center">
                    <Coins className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle>Crypto Deposit</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Send crypto to our address then submit the transaction hash</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {cryptoWallets.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">The administrator hasn&apos;t added any crypto top-up wallets yet. Please check back later.</p>
                ) : (
                <form onSubmit={submitCrypto} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Where are the funds coming from?</Label>
                    <Select value={cryptoSource} onValueChange={setCryptoSource}>
                      <SelectTrigger data-testid="crypto-source-select" className="h-11">
                        <SelectValue placeholder="Pick an exchange or wallet" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXCHANGE_PRESETS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {cryptoSource === 'Other' && (
                      <Input
                        placeholder="Enter source (exchange or wallet name)"
                        value={cryptoSourceOther}
                        onChange={(e) => setCryptoSourceOther(e.target.value)}
                        className="h-11 mt-2"
                        data-testid="crypto-source-other-input"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Deposit wallet (currency + network)</Label>
                    <Select value={cryptoWalletId} onValueChange={setCryptoWalletId}>
                      <SelectTrigger data-testid="crypto-wallet-select" className="h-11">
                        <SelectValue placeholder="Pick a wallet" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(cryptoByCurrency).flatMap(([cur, wallets]) => wallets.map((w) => (
                          <SelectItem key={w.id} value={w.id}>{cur} • {w.network}</SelectItem>
                        )))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedCryptoWallet && (
                    <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900 space-y-2" data-testid="crypto-wallet-details">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm text-muted-foreground">Send to this address on <strong className="text-foreground">{selectedCryptoWallet.network}</strong>:</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs font-mono bg-background rounded px-2 py-2 break-all" data-testid="crypto-address-display">{selectedCryptoWallet.address}</code>
                        <Button type="button" size="icon" variant="outline" onClick={() => copyAddress(selectedCryptoWallet.address)} data-testid="copy-crypto-address-btn"><Copy className="h-4 w-4" /></Button>
                      </div>
                      {selectedCryptoWallet.memo_tag && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-24">Memo/Tag:</span>
                          <code className="flex-1 text-xs font-mono bg-background rounded px-2 py-1">{selectedCryptoWallet.memo_tag}</code>
                          <Button type="button" size="icon" variant="outline" onClick={() => copyAddress(selectedCryptoWallet.memo_tag)}><Copy className="h-4 w-4" /></Button>
                        </div>
                      )}
                      {selectedCryptoWallet.instructions && (
                        <p className="text-xs whitespace-pre-wrap">{selectedCryptoWallet.instructions}</p>
                      )}
                      {selectedCryptoWallet.min_amount > 0 && (
                        <p className="text-xs text-muted-foreground">Minimum top up: {selectedCryptoWallet.min_amount} {selectedCryptoWallet.currency}</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Amount you sent</Label>
                    <Input
                      type="number" step="0.00000001" min="0"
                      value={cryptoAmount}
                      onChange={(e) => setCryptoAmount(e.target.value)}
                      placeholder="0.00"
                      className="h-11"
                      data-testid="crypto-amount-input"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Transaction hash or explorer link</Label>
                    <Input
                      value={cryptoTxHash}
                      onChange={(e) => setCryptoTxHash(e.target.value)}
                      placeholder="0xabc… or https://etherscan.io/tx/…"
                      className="h-11 font-mono"
                      data-testid="crypto-tx-hash-input"
                      required
                    />
                    <p className="text-xs text-muted-foreground">Admin will use this to verify your top-up before crediting.</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Note (optional)</Label>
                    <Textarea
                      rows={2}
                      value={cryptoNote}
                      onChange={(e) => setCryptoNote(e.target.value)}
                      placeholder="Anything the admin should know?"
                      data-testid="crypto-note-input"
                    />
                  </div>

                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Deposit will remain <strong>pending</strong> until the administrator verifies your transaction on-chain and approves it.
                    </p>
                  </div>

                  <Button type="submit" disabled={loading} className="w-full h-11" data-testid="submit-crypto-topup-button">
                    {loading ? 'Submitting...' : 'Submit for Approval'}
                  </Button>
                </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="agent" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                    <Banknote className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle>Cash Top up via Agent</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Visit a local agent to add cash to your wallet</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitAgentDeposit} className="space-y-6">
                  {/* Agent Selection */}
                  <div>
                    <Label>Select Agent</Label>
                    {selectedAgent ? (
                      <div className="mt-2 p-4 border rounded-lg bg-muted/30">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground">{selectedAgent.business_name || 'Agent'}</h4>
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {[selectedAgent.city, selectedAgent.province, selectedAgent.country].filter(Boolean).join(', ')}
                            </p>
                            {selectedAgent.phone && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <Phone className="h-3 w-3" />
                                {selectedAgent.phone}
                              </p>
                            )}
                          </div>
                          <Button type="button" size="sm" variant="outline" onClick={() => setSelectedAgent(null)}>Change</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 max-h-64 overflow-y-auto border rounded-lg divide-y">
                        {agents.length === 0 ? (
                          <p className="p-4 text-center text-sm text-muted-foreground">No agents available. Please try again later.</p>
                        ) : (
                          agents.map((agent) => (
                            <div key={agent.id} className="p-3 hover:bg-muted/50 cursor-pointer transition-colors" onClick={() => setSelectedAgent(agent)}>
                              <h4 className="font-medium text-sm">{agent.business_name || 'Agent'}</h4>
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <MapPin className="h-3 w-3" />
                                {[agent.city, agent.province, agent.country].filter(Boolean).join(', ')}
                              </p>
                              {agent.services && (
                                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                                  {Array.isArray(agent.services) 
                                    ? agent.services.slice(0, 2).join(', ')
                                    : typeof agent.services === 'string'
                                    ? agent.services.split(',').slice(0, 2).join(', ')
                                    : agent.services}
                                </p>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Currency */}
                  <div>
                    <Label htmlFor="agent-currency">Currency</Label>
                    <Select value={agentCurrency} onValueChange={setAgentCurrency}>
                      <SelectTrigger id="agent-currency">
                        <SelectValue placeholder="Choose currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Amount */}
                  <div>
                    <Label htmlFor="agent-amount">Amount</Label>
                    <Input id="agent-amount" type="number" step="0.01" min="1" placeholder="0.00" value={agentAmount} onChange={(e) => setAgentAmount(e.target.value)} />
                  </div>

                  {/* Payment Method */}
                  <div>
                    <Label htmlFor="agent-method">Payment Method</Label>
                    <Select value={agentMethod} onValueChange={setAgentMethod}>
                      <SelectTrigger id="agent-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank">Bank Transfer</SelectItem>
                        <SelectItem value="mobile_money">Mobile Money</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Optional Note */}
                  <div>
                    <Label htmlFor="agent-note">Note (Optional)</Label>
                    <Textarea id="agent-note" placeholder="Any additional instructions..." rows={2} value={agentNote} onChange={(e) => setAgentNote(e.target.value)} />
                  </div>

                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Next Steps:</strong> After submitting, the agent will contact you to arrange the top up. Bring cash to their location, they'll credit your wallet instantly.
                    </p>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading || !selectedAgent}>
                    {loading ? 'Submitting...' : 'Submit Top up Request'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
